import { Link } from '@tanstack/react-router'
import { LucideIcon } from 'components/LucideIcon'
import { SlideToggle } from 'components/SlideToggle'
import { formatDistanceToNow } from 'date-fns'
import { Box } from 'designSystem/components/Box'
import { Button } from 'designSystem/components/Button'
import React from 'react'
import styled from 'styled-components'

import {
  SORT_DIRECTION_CONFIG,
  type SortDirection,
} from './NotificationListPanel'
import { parseTimestamp, stripHtml } from './notificationUtils'

const NotificationsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
  margin-bottom: 0.5rem;
`

const ToggleLabel = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  user-select: none;
  min-width: 2.75rem;
  text-align: right;
`

const EmptyState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const LoadingState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ErrorState = styled.div`
  padding: 2rem 1rem;
  text-align: center;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.danger ?? theme.common.alert.error};
`

const NotificationTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.activeText};
  margin-bottom: 0.25rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const NotificationBody = styled.div`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 0.375rem;
`

const NotificationTimestamp = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const NotificationSubtitle = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 50%;
`

const NotificationItemContainer = styled.div<{ $isUnread: boolean }>`
  padding: 0.75rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  border-radius: 0.25rem;
  background-color: ${({ theme, $isUnread }) =>
    $isUnread ? theme.forecast.background2_5 : theme.forecast.background2};
  border-left: 3px solid
    ${({ theme, $isUnread }) =>
      $isUnread
        ? (theme.forecast.primary ?? theme.forecast.defaultButton)
        : 'transparent'};
  opacity: ${({ $isUnread }) => ($isUnread ? 1 : 0.7)};
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  cursor: pointer;
  position: relative;

  &:hover {
    background-color: ${({ theme }) => theme.forecast.background3};
  }
`

const ViewAllLink = styled(Link)`
  display: block;
  text-align: center;
  padding: 0.625rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.defaultText};
  text-decoration: none;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
  margin-top: 0.25rem;
  transition: all 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.forecast.activeText};
    background-color: ${({ theme }) => theme.forecast.background3};
  }
`

export function NotificationsDropdownContent({
  error,
  hasUnread,
  isLoading,
  onArchive,
  onClose,
  onMarkAllAsRead,
  onMarkAsRead,
  onNotificationClick,
  onShowAllChange,
  onSortDirectionChange,
  showAll,
  sortDirection,
  sortedNotifications,
}: {
  error: unknown
  hasUnread: boolean
  isLoading: boolean
  onArchive: (e: React.MouseEvent, notificationId: string) => void
  onClose: () => void
  onMarkAllAsRead: () => void
  onMarkAsRead: (e: React.MouseEvent, notificationId: string) => void
  onNotificationClick: (
    e: React.MouseEvent,
    notification: { id: string; cta?: { label: string; url: string } }
  ) => void
  onShowAllChange: (showAll: boolean) => void
  onSortDirectionChange: (sortDirection: SortDirection) => void
  showAll: boolean
  sortDirection: SortDirection
  sortedNotifications: Array<{
    id: string
    title?: string
    body?: string
    message?: string
    delivered_at?: string | null
    created_at?: string | null
    subtitle?: string
    isUnread: boolean
    cta?: { label: string; url: string }
  }>
}) {
  return (
    <>
      <NotificationsHeader>
        <Box display='flex' align='center' gap='xsPlus'>
          <span>Notifications</span>
          <Button
            variant='ghost'
            size='icon-sm'
            onClick={(e) => {
              e.preventDefault()
              onSortDirectionChange(SORT_DIRECTION_CONFIG[sortDirection].next)
            }}
            type='button'
            title={SORT_DIRECTION_CONFIG[sortDirection].label}
            aria-label={SORT_DIRECTION_CONFIG[sortDirection].ariaLabel}>
            <LucideIcon
              name={SORT_DIRECTION_CONFIG[sortDirection].icon}
              size={16}
            />
          </Button>
        </Box>
        <Box display='flex' align='center' gap='md'>
          {hasUnread && (
            <Button
              variant='ghost'
              size='sm'
              onClick={onMarkAllAsRead}
              type='button'
              aria-label='Mark all as read'>
              Mark all as read
            </Button>
          )}
          <Box display='flex' align='center' gap='sm'>
            <ToggleLabel>{showAll ? 'All' : 'Unread'}</ToggleLabel>
            <SlideToggle
              checked={showAll}
              onChange={onShowAllChange}
              ariaLabel='Toggle between all and unread notifications'
            />
          </Box>
        </Box>
      </NotificationsHeader>

      {isLoading ? (
        <LoadingState>Loading notifications...</LoadingState>
      ) : error ? (
        <ErrorState>Failed to load notifications</ErrorState>
      ) : sortedNotifications.length === 0 ? (
        <EmptyState>
          {showAll ? 'No notifications' : 'No unread notifications'}
        </EmptyState>
      ) : (
        <Box
          display='flex'
          direction='column'
          gap='xs'
          overflow='yAuto'
          style={{ maxHeight: '320px' }}>
          {sortedNotifications.map((notification) => (
            <NotificationItemContainer
              key={notification.id}
              $isUnread={notification.isUnread}
              onClick={(e) => onNotificationClick(e, notification)}>
              <Box flex='one' minWidth='none'>
                {notification.title && (
                  <NotificationTitle>{notification.title}</NotificationTitle>
                )}
                {notification.body && (
                  <NotificationBody>
                    {stripHtml(notification.body)}
                  </NotificationBody>
                )}
                {!notification.title && !notification.body && (
                  <NotificationBody>{notification.message}</NotificationBody>
                )}
                <Box
                  display='flex'
                  justify='between'
                  align='center'
                  style={{ marginTop: '0.25rem' }}>
                  {notification.delivered_at && (
                    <NotificationTimestamp>
                      {formatDistanceToNow(
                        parseTimestamp(notification.delivered_at),
                        {
                          addSuffix: true,
                        }
                      )}
                    </NotificationTimestamp>
                  )}
                  {!notification.delivered_at && notification.created_at && (
                    <NotificationTimestamp>
                      {formatDistanceToNow(
                        parseTimestamp(notification.created_at),
                        {
                          addSuffix: true,
                        }
                      )}
                    </NotificationTimestamp>
                  )}
                  {notification.subtitle && (
                    <NotificationSubtitle>
                      {notification.subtitle}
                    </NotificationSubtitle>
                  )}
                </Box>
              </Box>
              {notification.isUnread && (
                <Button
                  variant='raised'
                  size='sm'
                  onClick={(e) => onMarkAsRead(e, notification.id)}
                  type='button'
                  aria-label='Dismiss notification'>
                  <span>Dismiss</span>
                </Button>
              )}
              <Button
                variant='raised'
                size='icon-sm'
                onClick={(e) => onArchive(e, notification.id)}
                type='button'
                aria-label='Archive notification'>
                <LucideIcon name='Archive' size={12} />
              </Button>
            </NotificationItemContainer>
          ))}
        </Box>
      )}

      <ViewAllLink to='/notifications' onClick={onClose}>
        View all notifications
      </ViewAllLink>
    </>
  )
}
